import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { COLOR_CLASS, loadPeople, usePeople } from "@/lib/people";
import PersonChip from "@/estimating/components/PersonChip";

/** Settings › Sales — the AM / Estimator list with each person's initials + colour (used by every chip). */
export default function PeopleEditor() {
  const { people, colors } = usePeople();
  const [adding, setAdding] = useState("");
  const save = async (email, patch) => {
    try { await api.updatePerson(email, patch); await loadPeople(true); toast.success("Saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Not saved"); }
  };
  const live = people.filter((p) => p.active !== false);
  const team = live.filter((p) => p.is_am || p.is_est);
  const others = live.filter((p) => !p.is_am && !p.is_est);
  return (
    <section className="rounded-lg border border-border bg-card p-4 space-y-3" data-testid="settings-people-section">
      <h3 className="text-sm font-semibold text-foreground">Estimators &amp; Account Managers
        <span className="ml-1 text-xs font-normal text-muted-foreground">— initials and colour show on Bid Board, Basis of Design, Pipeline, Contracts and Reconcile</span></h3>
      <table className="w-full text-xs">
        <thead className="text-[11px] text-muted-foreground"><tr>
          <th className="py-1 text-left font-semibold">Person</th><th className="w-20 text-left font-semibold">Initials</th>
          <th className="w-12 text-center font-semibold">AM</th><th className="w-12 text-center font-semibold">Est</th>
          <th className="text-left font-semibold">Colour</th></tr></thead>
        <tbody>
          {team.map((p) => (
            <tr key={p.email} className="border-t border-border" data-testid={`people-row-${p.email}`}>
              <td className="py-1.5"><span className="inline-flex items-center gap-2"><PersonChip name={p.email} />{p.name}
                <span className="text-muted-foreground">{p.email}</span></span></td>
              <td><input defaultValue={p.initials} key={p.initials} maxLength={3} data-testid={`people-initials-${p.email}`}
                         onBlur={(e) => e.target.value.toUpperCase() !== p.initials && save(p.email, { initials: e.target.value })}
                         className="h-7 w-14 rounded border border-border bg-transparent px-2 text-xs uppercase outline-none focus:border-[#007BFF]" /></td>
              <td className="text-center"><Checkbox checked={p.is_am} disabled={p.role === "account_manager"} onCheckedChange={(v) => save(p.email, { is_am: !!v })} data-testid={`people-am-${p.email}`} /></td>
              <td className="text-center"><Checkbox checked={p.is_est} disabled={p.role === "estimator"} onCheckedChange={(v) => save(p.email, { is_est: !!v })} data-testid={`people-est-${p.email}`} /></td>
              <td><div className="flex flex-wrap gap-1">{colors.map((c) => (
                <button key={c} type="button" title={c} onClick={() => save(p.email, { avatar_color: c })} data-testid={`people-color-${p.email}-${c}`}
                        className={`h-4 w-4 rounded-full ${COLOR_CLASS[c]} ${p.color === c ? "ring-2 ring-offset-1 ring-[#007BFF]" : ""}`} />))}</div></td>
            </tr>))}
        </tbody>
      </table>
      <div className="flex items-center gap-2">
        <select value={adding} onChange={(e) => setAdding(e.target.value)} data-testid="people-add-select"
                className="h-7 rounded border border-border bg-transparent px-2 text-xs">
          <option value="">Add a person from Settings › Users…</option>
          {others.map((p) => <option key={p.email} value={p.email}>{p.name} ({p.email})</option>)}
        </select>
        {["am", "est"].map((k) => (
          <button key={k} type="button" disabled={!adding} data-testid={`people-add-${k}`}
                  onClick={() => { save(adding, k === "am" ? { is_am: true } : { is_est: true }); setAdding(""); }}
                  className="inline-flex h-7 items-center gap-1 rounded bg-[#007BFF] px-2.5 text-xs font-medium text-white disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" /> {k === "am" ? "AM" : "Estimator"}
          </button>))}
      </div>
    </section>
  );
}
